import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './Products.css';
import './AddSellStyles.css';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';

import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const Expenses = () => {
  const [activeTab, setActiveTab] = useState('list_expenses');

  // Shared State
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // List State
  const [expenses, setExpenses] = useState([]);
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [openActionId, setOpenActionId] = useState(null);

  // Metadata
  const [locations, setLocations] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Filter States
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Add Expense Form State
  const getCurrentDateTime = () => {
    return dateUtils.getChicagoISOString();
  };

  const [formData, setFormData] = useState({
    location_id: '',
    expense_category_id: '',
    sub_category: '',
    ref_no: '',
    expense_date: getCurrentDateTime(),
    expense_for: '',
    expense_for_contact_id: '',
    applicable_tax_id: '',
    total_amount: 0,
    tax_amount: 0,
    expense_note: '',
    is_refund: false,
    is_recurring: false,
    recurring_interval: '',
    recurring_interval_unit: 'Days',
    no_of_repetitions: '',
    document: null
  });

  // Payment section state
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    paid_on: getCurrentDateTime(),
    payment_method: 'cash',
    payment_account_id: '',
    payment_note: ''
  });

  // Category Management State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    code: '',
    description: ''
  });

  // Fetch metadata on mount
  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      const [locationsData, categoriesData, accountsData, usersData, contactsData, taxRatesData] = await Promise.all([
        apiService.getBusinessLocations(),
        apiService.getExpenseCategories(),
        apiService.getPaymentAccounts(),
        apiService.getUsers(),
        apiService.getContacts(),
        apiService.getTaxRates()
      ]);

      setLocations(Array.isArray(locationsData) ? locationsData : (locationsData.data || []));
      setExpenseCategories(Array.isArray(categoriesData) ? categoriesData : (categoriesData.data || []));
      setPaymentAccounts(Array.isArray(accountsData) ? accountsData : (accountsData.data || []));
      setUsers(Array.isArray(usersData) ? usersData : (usersData.data || []));
      setContacts(Array.isArray(contactsData) ? contactsData : (contactsData.data || []));
      setTaxRates(Array.isArray(taxRatesData) ? taxRatesData : (taxRatesData.data || []));

      // Set default payment methods
      setPaymentMethods([
        { id: 'cash', name: 'Cash' },
        { id: 'card', name: 'Card' },
        { id: 'cheque', name: 'Cheque' },
        { id: 'bank_transfer', name: 'Bank Transfer' },
        { id: 'other', name: 'Other' }
      ]);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  // Auto-calculate tax amount when tax rate is selected
  useEffect(() => {
    if (formData.applicable_tax_id && formData.total_amount) {
      const selectedTax = taxRates.find(tax => tax.id === parseInt(formData.applicable_tax_id));
      if (selectedTax) {
        const taxAmount = (parseFloat(formData.total_amount) * parseFloat(selectedTax.amount)) / 100;
        setFormData(prev => ({ ...prev, tax_amount: taxAmount }));
      }
    } else {
      setFormData(prev => ({ ...prev, tax_amount: 0 }));
    }
  }, [formData.applicable_tax_id, formData.total_amount, taxRates]);

  // Calculate final amount
  const finalAmount = (parseFloat(formData.total_amount) || 0) + (parseFloat(formData.tax_amount) || 0);

  // Calculate payment due
  const paymentDue = finalAmount - (parseFloat(paymentData.amount) || 0);

  const handleExport = (format) => {
    if (!expenses || expenses.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Date', 'Reference No', 'Expense Category', 'Location', 'Payment Status', 'Total Amount', 'Expense For', 'Added By'];
    const rows = expenses.map(e => [
      e.date || '',
      e.ref_no || '',
      e.category || '',
      e.location || '',
      e.payment_status || '',
      e.total_amount || '0.00',
      e.expense_for || '',
      e.added_by || ''
    ]);

    const title = 'Expenses List';
    const filename = 'expenses';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handlePrint = () => { printPage(); setShowExportDropdown(false); };
  const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };

  const handleAddClick = () => {
    setActiveTab('add_expense');
  };

  const handleCancelAdd = () => {
    setActiveTab('list_expenses');
    setFormData({
      location_id: '',
      expense_category_id: '',
      ref_no: '',
      expense_date: getCurrentDateTime(),
      expense_for: '',
      total_amount: 0,
      tax_id: '',
      tax_amount: 0,
      final_amount: 0,
      payment_status: 'due',
      payment_account_id: '',
      document: null,
      additional_notes: ''
    });
  };

  const handleSaveExpense = async () => {
    try {
      await apiService.createExpense(formData);
      alert('Expense created successfully!');
      handleCancelAdd();
      // Refresh expenses list
    } catch (error) {
      alert('Failed to create expense: ' + error.message);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, document: file });
  };

  // Category Management
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', code: '', description: '' });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      code: category.code || '',
      description: category.description || ''
    });
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await apiService.updateExpenseCategory(editingCategory.id, categoryFormData);
        alert('Category updated successfully!');
      } else {
        await apiService.createExpenseCategory(categoryFormData);
        alert('Category created successfully!');
      }
      setShowCategoryModal(false);
      fetchMetadata();
    } catch (error) {
      alert('Failed to save category: ' + error.message);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await apiService.deleteExpenseCategory(categoryId);
        alert('Category deleted successfully!');
        fetchMetadata();
      } catch (error) {
        alert('Failed to delete category: ' + error.message);
      }
    }
  };

  // RENDER ADD EXPENSE FORM
  if (activeTab === 'add_expense') {
    return (
      <div className="sell-form-container">
        <div className="page-header">
          <button
            onClick={handleCancelAdd}
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '40px',
              height: '40px'
            }}
          >
            ←
          </button>
          <h2 style={{ fontSize: '1.25rem' }}><i className="fa fa-plus"></i> Add Expense</h2>
        </div>

        <form className="sell-form" onSubmit={(e) => { e.preventDefault(); handleSaveExpense(); }}>

          {/* Basic Information Section */}
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
              <div className="form-group">
                <label>Business Location: *</label>
                <select
                  className="form-select"
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  required
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Expense Category:</label>
                <select
                  className="form-select"
                  value={formData.expense_category_id}
                  onChange={(e) => setFormData({ ...formData, expense_category_id: e.target.value })}
                >
                  <option value="">Select Category</option>
                  {expenseCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Sub category:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.sub_category}
                  onChange={(e) => setFormData({ ...formData, sub_category: e.target.value })}
                  placeholder="Enter sub category"
                />
              </div>

              <div className="form-group">
                <label>Reference No:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.ref_no}
                  onChange={(e) => setFormData({ ...formData, ref_no: e.target.value })}
                  placeholder="Leave empty to autogenerate"
                />
              </div>

              <div className="form-group">
                <label>Date: *</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Expense for:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.expense_for}
                  onChange={(e) => setFormData({ ...formData, expense_for: e.target.value })}
                  placeholder="Person or purpose"
                />
              </div>

              <div className="form-group">
                <label>Expense for contact:</label>
                <select
                  className="form-select"
                  value={formData.expense_for_contact_id}
                  onChange={(e) => setFormData({ ...formData, expense_for_contact_id: e.target.value })}
                >
                  <option value="">Select Contact</option>
                  {contacts.map(contact => (
                    <option key={contact.id} value={contact.id}>{contact.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Attach Document:</label>
                <input
                  type="file"
                  className="form-input"
                  onChange={handleFileChange}
                  accept=".pdf,.csv,.zip,.doc,.docx,.jpeg,.jpg,.png"
                />
                <small style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                  Max File size: 5MB<br />
                  Allowed File: .pdf, .csv, .zip, .doc, .docx, .jpeg, .jpg, .png
                </small>
              </div>
            </div>
          </div>

          {/* Expense Details Section */}
          <div className="form-section">
            <h3 className="section-title">Expense Details</h3>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
              <div className="form-group">
                <label>Applicable Tax:</label>
                <select
                  className="form-select"
                  value={formData.applicable_tax_id}
                  onChange={(e) => setFormData({ ...formData, applicable_tax_id: e.target.value })}
                >
                  <option value="">None</option>
                  {taxRates.map(tax => (
                    <option key={tax.id} value={tax.id}>{tax.name} ({tax.amount}%)</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Total amount: *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.total_amount}
                  onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                  placeholder="Total amount"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label>Tax Amount:</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.tax_amount.toFixed(2)}
                  readOnly
                  style={{ backgroundColor: '#f8fafc' }}
                />
              </div>

              <div className="form-group">
                <label>Final Amount:</label>
                <input
                  type="number"
                  className="form-input"
                  value={finalAmount.toFixed(2)}
                  readOnly
                  style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Expense note:</label>
                <textarea
                  className="form-textarea"
                  value={formData.expense_note}
                  onChange={(e) => setFormData({ ...formData, expense_note: e.target.value })}
                  rows="3"
                  placeholder="Enter expense notes"
                ></textarea>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_refund}
                    onChange={(e) => setFormData({ ...formData, is_refund: e.target.checked })}
                  />
                  Is refund?
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                  />
                  Is Recurring?
                </label>
              </div>
            </div>
          </div>

          {/* Recurring Settings Section (Conditional) */}
          {formData.is_recurring && (
            <div className="form-section">
              <h3 className="section-title">Recurring Settings</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Recurring interval: *</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.recurring_interval}
                      onChange={(e) => setFormData({ ...formData, recurring_interval: e.target.value })}
                      placeholder="Enter interval"
                      style={{ flex: 1 }}
                      required={formData.is_recurring}
                    />
                    <select
                      className="form-select"
                      value={formData.recurring_interval_unit}
                      onChange={(e) => setFormData({ ...formData, recurring_interval_unit: e.target.value })}
                      style={{ minWidth: '100px', flex: '0 0 auto' }}
                    >
                      <option value="Days">Days</option>
                      <option value="Weeks">Weeks</option>
                      <option value="Months">Months</option>
                      <option value="Years">Years</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>No. of Repetitions:</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.no_of_repetitions}
                    onChange={(e) => setFormData({ ...formData, no_of_repetitions: e.target.value })}
                    placeholder="Leave blank for infinite"
                  />
                  <small style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                    If blank expense will be generated infinite times
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Payment Section Toggle */}
          <div className="form-section">
            <button
              type="button"
              onClick={() => setShowPaymentSection(!showPaymentSection)}
              style={{
                marginBottom: '1rem',
                padding: '0.5rem 1.25rem',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                whiteSpace: 'nowrap'
              }}
            >
              <i className={`fa fa-${showPaymentSection ? 'minus' : 'plus'}`}></i>
              {showPaymentSection ? 'Hide Payment' : 'Add payment'}
            </button>

            {/* Payment Information Section (Conditional) */}
            {showPaymentSection && (
              <>
                <h3 className="section-title">Payment Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount: *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      required={showPaymentSection}
                    />
                  </div>

                  <div className="form-group">
                    <label>Paid on: *</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={paymentData.paid_on}
                      onChange={(e) => setPaymentData({ ...paymentData, paid_on: e.target.value })}
                      required={showPaymentSection}
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Method: *</label>
                    <select
                      className="form-select"
                      value={paymentData.payment_method}
                      onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                      required={showPaymentSection}
                    >
                      {paymentMethods.map(method => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Payment Account:</label>
                    <select
                      className="form-select"
                      value={paymentData.payment_account_id}
                      onChange={(e) => setPaymentData({ ...paymentData, payment_account_id: e.target.value })}
                    >
                      <option value="">Select Account</option>
                      {paymentAccounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Payment note:</label>
                    <textarea
                      className="form-textarea"
                      value={paymentData.payment_note}
                      onChange={(e) => setPaymentData({ ...paymentData, payment_note: e.target.value })}
                      rows="2"
                      placeholder="Enter payment notes"
                    ></textarea>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Payment due:</label>
                    <input
                      type="number"
                      className="form-input"
                      value={paymentDue.toFixed(2)}
                      readOnly
                      style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', color: '#92400e' }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons-section" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '2rem', padding: '0.5rem 0' }}>
            <button type="submit" className="save-btn">
              💾 Save Expense
            </button>
            <button type="button" className="save-print-btn" onClick={handleCancelAdd}>
              ✕ Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // RENDER EXPENSE CATEGORIES TAB
  if (activeTab === 'expense_categories') {
    return (
      <div className="products-container">
        <div className="main-card">
          <div className="product-tabs">
            <div
              className={`product-tab ${activeTab === 'list_expenses' ? 'active' : ''}`}
              onClick={() => setActiveTab('list_expenses')}
            >
              <i className="fa fa-list"></i> List Expenses
            </div>

            <div
              className={`product-tab ${activeTab === 'expense_categories' ? 'active' : ''}`}
              onClick={() => setActiveTab('expense_categories')}
            >
              <i className="fa fa-tags"></i> Expense Categories
            </div>
          </div>

          <div className="table-controls">
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="tabs-search-input"
                  style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '0.9rem' }}
                />
              </div>
              <button className="control-btn-primary" onClick={handleAddCategory}>
                <span className="plus-icon">+</span> Add
              </button>
            </div>
          </div>

          <div className="table-content-card">
            <div className="products-table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Category Name</th>
                    <th>Category Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {expenseCategories.length === 0 ? (
                    <tr><td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>No categories found.</td></tr>
                  ) : (
                    expenseCategories
                      .filter(cat => cat.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map(category => (
                        <tr key={category.id}>
                          <td className="actions-cell">
                            <div className="action-dropdown-container">
                              <button
                                className="action-btn-trigger"
                                onClick={() => setOpenActionId(openActionId === category.id ? null : category.id)}
                              >
                                Actions <span className="arrow-down">▼</span>
                              </button>
                              {openActionId === category.id && (
                                <div className="action-dropdown-menu">
                                  <button onClick={() => handleEditCategory(category)}>Edit</button>
                                  <button onClick={() => handleDeleteCategory(category.id)}>Delete</button>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{category.name}</td>
                          <td>{category.code || 'N/A'}</td>
                          <td>{category.description || 'N/A'}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Category Add/Edit Modal */}
        {showCategoryModal && (
          <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%' }}>
              <div className="modal-header">
                <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
                <button className="modal-close-btn" onClick={() => setShowCategoryModal(false)}>✕</button>
              </div>
              <div className="modal-content" style={{ padding: '1.5rem' }}>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Category Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Category Code</label>
                  <input
                    type="text"
                    className="form-input"
                    value={categoryFormData.code}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, code: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-textarea"
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                    rows="3"
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn close-btn" onClick={() => setShowCategoryModal(false)}>Cancel</button>
                <button className="modal-btn primary-btn" onClick={handleSaveCategory}>Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // RENDER LIST EXPENSES TAB (DEFAULT)
  return (
    <div className="products-container">
      <div className="main-card">
        <div className="product-tabs">
          <div
            className={`product-tab ${activeTab === 'list_expenses' ? 'active' : ''}`}
            onClick={() => setActiveTab('list_expenses')}
          >
            <i className="fa fa-list"></i> List Expenses
          </div>

          <div
            className={`product-tab ${activeTab === 'expense_categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('expense_categories')}
          >
            <i className="fa fa-tags"></i> Expense Categories
          </div>
        </div>

        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={handleAddClick}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        <div className="table-content-card">
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Date</th>
                  <th>Reference No</th>
                  <th>Expense Category</th>
                  <th>Location</th>
                  <th>Payment Status</th>
                  <th>Total Amount</th>
                  <th>Expense For</th>
                  <th>Added By</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No expenses found.</td></tr>
                ) : (
                  expenses.map(expense => (
                    <tr key={expense.id}>
                      <td className="actions-cell">
                        <div className="action-dropdown-container">
                          <button
                            className="action-btn-trigger"
                            onClick={() => setOpenActionId(openActionId === expense.id ? null : expense.id)}
                          >
                            Actions <span className="arrow-down">▼</span>
                          </button>
                          {openActionId === expense.id && (
                            <div className="action-dropdown-menu">
                              <button onClick={() => console.log('View', expense.id)}>View</button>
                              <button onClick={() => console.log('Edit', expense.id)}>Edit</button>
                              <button onClick={() => console.log('Delete', expense.id)}>Delete</button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{expense.date}</td>
                      <td>{expense.ref_no}</td>
                      <td>{expense.category}</td>
                      <td>{expense.location}</td>
                      <td>{expense.payment_status}</td>
                      <td>${expense.total_amount}</td>
                      <td>{expense.expense_for}</td>
                      <td>{expense.added_by}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
            <div>Showing 0 to 0 of 0 entries</div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="pagination-btn" disabled>Previous</button>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>Page 1 of 1</span>
              <button className="pagination-btn" disabled>Next</button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Business Location:</label>
                  <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}>
                    <option value="all">All</option>
                    {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Expense Category:</label>
                  <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                    <option value="all">All</option>
                    {expenseCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Payment Status:</label>
                  <select value={filterPaymentStatus} onChange={(e) => setFilterPaymentStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="due">Due</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>
              </div>
              <div className="filter-row second-row">
                <div className="filter-group">
                  <label>Start Date:</label>
                  <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label>End Date:</label>
                  <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
              <button className="modal-btn primary-btn">Apply Filters</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
